import { atom, useAtom } from "jotai"
import React, { ReactNode } from "react"
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from "@/components/ui/ContextMenu"
import { store } from "@/store/store"

const contextMenuState = atom({
	key: 0,
	event: null as MouseEvent | null,
	content: null as ReactNode | null,
	root: null as Element | null,
})

export const showContextMenu = (e: React.MouseEvent, content: ReactNode) => {
	e.preventDefault()

	const target = isHTMLorSVGElement(e.target) ? e.target : null
	const root = target?.closest(".menu-root") ?? null

	const menuEvent = new MouseEvent("contextmenu", {
		bubbles: true,
		cancelable: true,
		view: window,
		button: 2,
		buttons: 0,
		clientX: e.clientX,
		clientY: e.clientY,
	})

	const menuState = store.get(contextMenuState)

	if (isHTMLorSVGElement(root)) {
		root.dataset.selected = ""
	}

	store.set(contextMenuState, {
		// key is incremented to make sure component is remounted
		key: menuState.key + 1,
		content: content,
		event: menuEvent,
		root,
	})
}

export const ContextMenuManager = () => {
	const [menu] = useAtom(contextMenuState)

	const onMenuRender = (instance: HTMLSpanElement | null) => {
		// Check if menu event is queued
		if (instance && menu.event) {
			instance.dispatchEvent(menu.event)
			menu.event = null
		}
	}

	const onOpenChange = (open: boolean) => {
		if (!open && isHTMLorSVGElement(menu.root)) {
			delete menu.root.dataset.selected
		}
	}

	return (
		<ContextMenu key={menu.key} onOpenChange={onOpenChange}>
			<ContextMenuTrigger ref={onMenuRender} />
			<ContextMenuContent>{menu.content}</ContextMenuContent>
		</ContextMenu>
	)
}

const isHTMLorSVGElement = (el: unknown): el is HTMLElement | SVGElement => {
	return el instanceof Element && "dataset" in el
}
